import { useMemo } from 'react';
import { type Control, type FieldErrors, useWatch } from 'react-hook-form';
import { useGetManagerQuery, useGetPendingManagerChangeReservationQuery } from '@/common/graphql';
import type { EmployeeGeneralInformationFormDataType } from '@/common/types';
import { ManagerFields } from './ManagerFields';
import { PendingManagerChanges } from './PendingManagerChanges';

interface Props {
  control: Control<EmployeeGeneralInformationFormDataType>;
  employeeId: string;
  errors: FieldErrors<EmployeeGeneralInformationFormDataType>;
}

export const Managers = ({ control, employeeId, errors }: Props) => {
  const primaryManagerId = useWatch({ control, name: 'primaryManagerId' });
  const dottedLineManagerId = useWatch({ control, name: 'dottedLineManagerId' });

  const { data: primaryManagerData, loading: loadingPrimaryManager } = useGetManagerQuery({
    variables: { id: primaryManagerId || '' },
    skip: !primaryManagerId,
  });
  const { data: dottedLineManagerData, loading: loadingDottedLineManager } = useGetManagerQuery({
    variables: { id: dottedLineManagerId || '' },
    skip: !dottedLineManagerId,
  });
  const {
    data,
    loading: loadingPendingChanges,
    refetch,
  } = useGetPendingManagerChangeReservationQuery({
    variables: { employeeId },
    skip: !employeeId,
  });

  const loading = useMemo(
    () => loadingPrimaryManager || loadingDottedLineManager || loadingPendingChanges,
    [loadingPrimaryManager, loadingDottedLineManager, loadingPendingChanges],
  );

  if (loading) {
    return null;
  }

  if (!data?.pendingManagerChangeReservation) {
    return (
      <ManagerFields
        control={control}
        dottedLineManager={dottedLineManagerData?.employee}
        employeeId={employeeId}
        errors={errors}
        loading={loading}
        primaryManager={primaryManagerData?.employee}
      />
    );
  }

  return (
    <PendingManagerChanges
      primaryManager={primaryManagerData?.employee}
      dottedLineManager={dottedLineManagerData?.employee}
      reservation={data.pendingManagerChangeReservation}
      refetch={refetch}
    />
  );
};
